import { useState, useEffect, useRef, useCallback } from 'react';
import { readCache, writeCache } from '../services/storage/readCache';

/**
 * useCachedRead — canonical DB-first read (stale-while-revalidate).
 *
 * Paints the cached value synchronously on mount (useState initializer — zero
 * flash), then fetches the DB (source of truth) and overwrites cache + state.
 * On a failed refresh the stale data stays on screen and the error surfaces —
 * the cache is never overwritten with a failure default (No Fallbacks: this is
 * cache-paint + revalidate, not a fallback).
 *
 * CONTRACT: fetcher must THROW/reject on failure. A fetcher that returns a
 * benign default ({}/[]/null) on error would cache emptiness over good data.
 *
 * @param {string} cacheKey - localStorage key (see localstorage-reference.md)
 * @param {() => Promise<any>} fetcher - fetches the fresh value; throws on failure
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] - gate the read. Disabled → data null,
 *   no cache paint, no fetch (a stale paint for a turned-off feature is a leak).
 *   Flipping to enabled mid-session fetches fresh (no late cache paint).
 * @param {boolean} [options.skipCachePaint=false] - volatile data: never paint
 *   the cache, fetch-only.
 * @returns {{
 *   data: any,
 *   loaded: boolean,      // data exists (from cache OR fresh) — gate empty states on loaded && isEmpty(data)
 *   refreshing: boolean,  // a revalidation is in flight
 *   error: Error|null,    // last refresh failure (stale data stays on screen)
 *   refresh: () => void,  // manual revalidate
 *   mutate: (valueOrUpdater: any) => void  // set state + cache together (mutation flows)
 * }}
 */
const useCachedRead = (cacheKey, fetcher, { enabled = true, skipCachePaint = false } = {}) => {
  // Synchronous first paint, gated on enabled — a disabled hook must never
  // paint stale data (e.g. peer locations after sharing was turned off).
  const [state, setState] = useState(() => {
    if (enabled && !skipCachePaint) {
      const cached = readCache(cacheKey);
      if (cached.hit) return { data: cached.value, loaded: true };
    }
    return { data: null, loaded: false };
  });
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Refs so inline fetchers don't re-fire the effect and mutate stays pure.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const dataRef = useRef(state.data);
  dataRef.current = state.data;

  useEffect(() => {
    if (!enabled) {
      // Clear any previously-painted data when the gate closes (same-object
      // return bails the re-render when already clear).
      setState((s) => (s.data === null && !s.loaded ? s : { data: null, loaded: false }));
      return undefined;
    }

    let cancelled = false;
    setRefreshing(true);

    Promise.resolve()
      .then(() => fetcherRef.current())
      .then((fresh) => {
        if (cancelled) return;
        writeCache(cacheKey, fresh);
        setError(null);
        setState({ data: fresh, loaded: true });
        setRefreshing(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[CACHED_READ] refresh failed:', cacheKey, err?.message);
        setError(err);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, enabled, refreshTick]);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  const mutate = useCallback(
    (valueOrUpdater) => {
      const next =
        typeof valueOrUpdater === 'function' ? valueOrUpdater(dataRef.current) : valueOrUpdater;
      writeCache(cacheKey, next);
      setState({ data: next, loaded: true });
    },
    [cacheKey]
  );

  return { data: state.data, loaded: state.loaded, refreshing, error, refresh, mutate };
};

export default useCachedRead;
